import { useCallback, useEffect, useRef } from "react";

import { useDataMeshTracking } from "../tracking";
import { useDatameshNamespace } from "./DatameshNamespaceProvider";
import { createEventHandlers } from "./eventHandlers";
import type { EventHandlers, TypesOfTrackingEvents } from "./types";
import { addHTMLEventID, buildTrackingObj } from "./utils";

const defaultHandlers = { effect: () => {}, cleanup: () => {} };

export const useTrackElement = <T extends HTMLElement = HTMLDivElement>(
  eventsToTrack: TypesOfTrackingEvents[],
  eventId: string,
  namespace?: string,
) => {
  const initialRefState = null;
  const trackingRef = useRef<T | null>(initialRefState);
  const datameshContextNamespace = useDatameshNamespace();
  const dataMeshTrackingClient = useDataMeshTracking();
  const eventHandlersRef = useRef<EventHandlers<T> | null>(null);
  eventHandlersRef.current =
    eventHandlersRef.current ?? createEventHandlers(dataMeshTrackingClient);
  const eventsToTrackRef = useRef(eventsToTrack);

  useEffect(() => {
    const trackingObj = buildTrackingObj(
      namespace ?? datameshContextNamespace,
      eventId,
    );

    if (trackingRef.current === initialRefState) return;
    const eventHandlers = eventHandlersRef.current;

    const element = trackingRef.current;
    addHTMLEventID(element, trackingObj.id);

    const eventHandlerFunctions = eventsToTrackRef.current.map((trackEvent) => {
      if (!eventHandlers) {
        return defaultHandlers;
      }
      return eventHandlers[trackEvent]({ element, trackingObj });
    });

    eventHandlerFunctions.forEach(({ effect }) => effect());

    return () => {
      eventHandlerFunctions.forEach(({ cleanup }) => {
        cleanup();
      });
    };
  }, [
    datameshContextNamespace,
    eventId,
    eventsToTrackRef,
    namespace,
    trackingRef,
  ]);

  return trackingRef;
};

export const useConditionalTrackElement = <
  T extends HTMLElement = HTMLDivElement,
>(
  eventsToTrack: TypesOfTrackingEvents[],
  eventId: string,
  namespace?: string,
) => {
  const datameshContextNamespace = useDatameshNamespace();
  const { trackClick, trackComponentDisplayed } = useDataMeshTracking();
  const elRef = useRef<T | null>(null);
  const eventsRef = useRef(eventsToTrack);

  return useCallback(
    (element: T) => {
      const trackingObj = buildTrackingObj(
        namespace ?? datameshContextNamespace,
        eventId,
      );
      const trackClickEvent = () => trackClick(trackingObj);

      if (element != null) {
        elRef.current = element;
        addHTMLEventID(element, trackingObj.id);
        if (eventsRef.current.includes("click")) {
          element.addEventListener("click", trackClickEvent);
        }
        if (eventsRef.current.includes("display")) {
          trackComponentDisplayed(trackingObj);
        }
      } else {
        if (elRef.current && eventsRef.current.includes("click")) {
          elRef.current.removeEventListener("click", trackClickEvent);
        }
        elRef.current = null;
      }

      return () => {
        if (eventsRef.current.includes("click")) {
          element.removeEventListener("click", trackClickEvent);
        }
      };
    },
    [
      namespace,
      datameshContextNamespace,
      eventId,
      trackClick,
      trackComponentDisplayed,
    ],
  );
};
